export interface LanguageDefinition {
  /** BCP 47 locale: html lang, LOCALE_ID, translation file, Angular and date-fns locale tables. */
  readonly locale: LanguageAvailable;
  /** Drupal langcode, sent to the CMS and to Elasticsearch. Never derived from the locale. */
  readonly langcode: LangcodeAvailable;
  /** Public URL prefix within its domain, null when the language is served at the domain root. */
  readonly urlPrefix: string | null;
  readonly region: RegionAvailable;
}

export interface RegionDefinition {
  readonly region: RegionAvailable;
  /** Registrable domain, matched as a suffix of the request host (preview and generation hosts included). */
  readonly domain: string;
  /** Local development host, declared in allowedHosts. */
  readonly devHost: string;
  readonly postalCodePattern: string;
  readonly phonePattern: string;
}

const languages = {
  'fr-FR': { locale: 'fr-FR', langcode: 'fr', urlPrefix: null, region: 'FR' },
  'es-ES': { locale: 'es-ES', langcode: 'es', urlPrefix: 'es', region: 'ES' },
  'ca-ES': { locale: 'ca-ES', langcode: 'ca', urlPrefix: 'ca', region: 'ES' },
  'fr-BE': { locale: 'fr-BE', langcode: 'fr-be', urlPrefix: 'fr-be', region: 'BE' },
  'nl-BE': { locale: 'nl-BE', langcode: 'nl-be', urlPrefix: 'nl-be', region: 'BE' },
} as const;

const regions = {
  FR: {
    region: 'FR',
    domain: 'socooc.com',
    devHost: 'socooc.dev.com.localhost',
    postalCodePattern: '^\\d{5}$',
    phonePattern: '^0[1-9]\\d{8}$',
  },
  ES: {
    region: 'ES',
    domain: 'socooc.es',
    devHost: 'socooc.dev.es.localhost',
    postalCodePattern: '^\\d{5}$',
    phonePattern: '^[6-9]\\d{8}$',
  },
  BE: {
    region: 'BE',
    domain: 'socooc.be',
    devHost: 'socooc.dev.be.localhost',
    postalCodePattern: '^[1-9]\\d{3}$',
    phonePattern: '^0(?:4\\d{8}|[1-9]\\d{7})$',
  },
} as const;

export type LanguageAvailable = keyof typeof languages;
export type LangcodeAvailable = (typeof languages)[LanguageAvailable]['langcode'];
export type RegionAvailable = keyof typeof regions;

// Annotated apart from the literals the key unions are read from, since annotating the literals is circular. The
// mapped types reject an entry whose locale or region is not its own key.
export const LANGUAGES: { readonly [L in LanguageAvailable]: LanguageDefinition & { readonly locale: L } } = languages;
export const REGIONS: { readonly [R in RegionAvailable]: RegionDefinition & { readonly region: R } } = regions;

/** Language designated by a path that starts with no language prefix, and translation fallback. */
export const LANGUAGE_FALLBACK: LanguageAvailable = 'fr-FR';
